'use client';

import { FC, useEffect, useState } from 'react';
import { Content, isFilled } from '@prismicio/client';
import { SliceComponentProps, PrismicRichText } from '@prismicio/react';
import { PrismicNextImage, PrismicNextLink } from '@prismicio/next';
import { Heading } from '@/components/common/Heading';
import dynamic from 'next/dynamic';
import { ArrowRightIcon } from '@/assets';
import { CheckMarkIcon } from '@/assets/CheckMarkIcon';
import DynamicMarquee from '@/components/DynamicMarquee';

const FloatingShapesBackground = dynamic(
  () => import('@/components/FloatingShapesBackground'),
  { ssr: false },
);

/**
 * Props for `SectionServicesGrid`.
 */
export type SectionServicesGridProps =
  SliceComponentProps<Content.HeroWithServicesGridSlice>;

/**
 * Component for "SectionServicesGrid" Slices.
 */
const SectionServicesGrid: FC<SectionServicesGridProps> = ({ slice }) => {
  const { title, description, primary_cta, services, technologies } =
    slice.primary;

  const [isClient, setIsClient] = useState(false);

  useEffect(() => {
    setIsClient(true);
  }, []);

  const duplicatedTechnologies = [...technologies, ...technologies];

  const renderTechnologyItem = (
    item: Content.HeroWithServicesGridSlice['primary']['technologies'][number],
    uniqueKey: string,
  ) => (
    // Each technology item needs to be flex-shrink-0 to prevent shrinking
    // and have horizontal margins for spacing between items.
    <div key={uniqueKey} className="flex-shrink-0 mx-5">
      {' '}
      {/* Added mx-5 for spacing */}
      {isFilled.image(item.technology_icon) && (
        <PrismicNextImage
          field={item.technology_icon}
          className="h-8 w-auto object-contain opacity-70 transition-opacity hover:opacity-100"
          alt=""
        />
      )}
    </div>
  );

  return (
    <section
      data-slice-type={slice.slice_type}
      data-slice-variation={slice.variation}
      className="relative overflow-hidden py-20 text-white px-[9rem]"
      style={{
        backgroundImage: 'linear-gradient(to bottom, #0F423A), #0E1010',
        paddingTop: '80px',
      }}
    >
      <FloatingShapesBackground />
      <div className="relative container mx-auto flex flex-col items-center px-4 text-center">
        <div className="flex flex-col items-start text-start gap-8">
          <Heading as="h2" size="lg" className="">
            <PrismicRichText field={title} />
          </Heading>
          <div className="text-lg text-gray-300">
            <PrismicRichText field={description} />
          </div>
          {isFilled.link(primary_cta) && (
            <PrismicNextLink field={primary_cta} className="secondary-button">
              <ArrowRightIcon className="arrow-icon" />
              {primary_cta.text ? primary_cta.text : 'Learn More'}
            </PrismicNextLink>
          )}
        </div>

        {/* Services Grid */}
        {services && services.length > 0 && (
          <div className="flex flex-grow-1 mt-16 grid w-full grid-cols-1 gap-6 md:grid-cols-2">
            {services.map((item) => (
              <div
                key={item.service_title}
                className="flex items-center rounded-xl bg-white/20 p-4 backdrop-blur-md hover:border-[2px] hover:border-b-[6px] hover:p-0 hover:border-[#05F273]"
              >
                <CheckMarkIcon width={20} height={8} />

                <span className="pl-1 text-base font-medium text-white font-bold">
                  {item.service_title}
                </span>
              </div>
            ))}
          </div>
        )}

        {/* Technologies Carousel */}
        {isClient && technologies?.length > 0 && (
          <div
            className="relative z-10 w-[98%] mx-auto mt-20 mb-[4rem] overflow-hidden"
            style={{
              WebkitMaskImage:
                'linear-gradient(to right, transparent 0%, black 10%, black 90%, transparent 100%)',
              maskImage:
                'linear-gradient(to right, transparent 0%, black 10%, black 90%, transparent 100%)',
              WebkitMaskRepeat: 'no-repeat',
              maskRepeat: 'no-repeat',
            }}
          >
            <DynamicMarquee speed={50} gradient={false} play={true}>
              {duplicatedTechnologies.map((item, index) => (
                <div
                  key={index}
                  className="relative flex flex-col items-center group px-4"
                >
                  {renderTechnologyItem(
                    item,
                    `${item.technology_name}-${index}`,
                  )}
                </div>
              ))}
            </DynamicMarquee>
          </div>
        )}
      </div>
    </section>
  );
};

export default SectionServicesGrid;
